import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './index.css';
import { bootstrapStore } from './lib/store';
import { seedIfEmpty } from './lib/seed';
import { start as startSync } from './lib/sync';

// onnxruntime-web checks for SharedArrayBuffer at module evaluation time
// and crashes if undefined. We run single-threaded WASM + WebGPU so real
// SAB is never used, but the constructor must exist. Aliased to ArrayBuffer.
// Must be set BEFORE any transformers.js/onnx import, including the lazy
// embedder in seed.ts.
if (typeof (globalThis as any).SharedArrayBuffer === 'undefined') {
  (globalThis as any).SharedArrayBuffer = ArrayBuffer;
}

// ReadableStream async iteration - not exposed on some iOS Safari builds
// even when advertised. kokoro-js's bundled espeak-ng decompresses dict
// files with `for await (const chunk of blob.stream().pipeThrough(ds))`
// which throws "undefined is not a function" without this.
if (typeof ReadableStream !== 'undefined' && !(ReadableStream.prototype as any)[Symbol.asyncIterator]) {
  (ReadableStream.prototype as any)[Symbol.asyncIterator] = function () {
    const reader = this.getReader();
    return {
      next: () => reader.read(),
      return: (value: any) => { reader.releaseLock(); return Promise.resolve({ value, done: true }); },
      [Symbol.asyncIterator]() { return this; },
    };
  };
}

// Polyfill TC39 stage-4 method that pdfjs-dist 5.x relies on.
// iOS Safari < 18.4 doesn't have it, causing PDF parsing to throw.
if (!(Map.prototype as any).getOrInsertComputed) {
  (Map.prototype as any).getOrInsertComputed = function (key: any, fn: (k: any) => any) {
    if (!this.has(key)) this.set(key, fn(key));
    return this.get(key);
  };
}
if (!(Map.prototype as any).getOrInsert) {
  (Map.prototype as any).getOrInsert = function (key: any, value: any) {
    if (!this.has(key)) this.set(key, value);
    return this.get(key);
  };
}
if (!(WeakMap.prototype as any).getOrInsertComputed) {
  (WeakMap.prototype as any).getOrInsertComputed = function (key: any, fn: (k: any) => any) {
    if (!this.has(key)) this.set(key, fn(key));
    return this.get(key);
  };
}
if (!(WeakMap.prototype as any).getOrInsert) {
  (WeakMap.prototype as any).getOrInsert = function (key: any, value: any) {
    if (!this.has(key)) this.set(key, value);
    return this.get(key);
  };
}
// Promise.withResolvers - widely used by pdfjs-dist 4.x+, missing on iOS < 17.4
if (!(Promise as any).withResolvers) {
  (Promise as any).withResolvers = function () {
    let resolve!: (v?: any) => void;
    let reject!: (e?: any) => void;
    const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
    return { promise, resolve, reject };
  };
}

// Debug overlays removed. Keep __doomLog as a no-op so existing callers
// in kokoro-server/kokoro don't throw. Errors now go to console only.
(window as any).__doomLog = (_msg: string) => {};

(async () => {
  await seedIfEmpty();
  await bootstrapStore();
  startSync();

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </React.StrictMode>,
  );
})();
